import {
	// eslint-disable-next-line jira/restricted/@atlassian/react-resource-router
	createResource,
	useResource,
	type RouteResource,
	type UseResourceHookResponse,
} from '@atlassian/react-resource-router';
import { get as userDismissGet } from './bannerDismissalUserProperty.tsx';
import { getAnnouncementBannerSettings } from './getAnnouncementBannerSettings.tsx';
import { getAnnouncementBannerSettingsV4 } from './getAnnouncementBannerSettingsV4.tsx';
import type {
	SettingsGetResource,
	SettingsGetV4Resource,
	SettingsGet,
	SettingsV4Get,
} from './SettingsGet.types.tsx';

export function getIsDismissed(
	isAnonymous: boolean,
	settings: SettingsGet | SettingsV4Get,
	hashIdLastDimissed: string | null,
): boolean {
	if (isAnonymous || !settings?.isDismissable) {
		return false;
	}

	return settings?.hashId === hashIdLastDimissed;
}

const announcementBannerSettingsResource = createResource<SettingsGetResource>({
	type: 'ANNOUNCEMENT_BANNER_SETTINGS',
	getKey: () => 'settings',
	getData: async (_, { tenantContext: { atlassianAccountId, isAnonymous } }) => {
		const [settings, hashIdLastDimissed] = await Promise.all([
			getAnnouncementBannerSettings(),
			userDismissGet(atlassianAccountId),
		]);

		return {
			...settings,
			isDismissable: isAnonymous ? false : settings?.isDismissable,
			isDismissed: getIsDismissed(isAnonymous, settings, hashIdLastDimissed),
		};
	},
	maxAge: 15 * 60 * 1000,
});

export const announcementBannerSettingsV4Resource: RouteResource<SettingsGetV4Resource> =
	createResource<SettingsGetV4Resource>({
		type: 'ANNOUNCEMENT_BANNER_SETTINGS_V4',
		getKey: () => 'settings',
		getData: async (_, { tenantContext: { atlassianAccountId, isAnonymous } }) => {
			const [settings, hashIdLastDimissed] = await Promise.all([
				getAnnouncementBannerSettingsV4(),
				userDismissGet(atlassianAccountId),
			]);

			return {
				...settings,
				isDismissable: isAnonymous ? false : settings?.isDismissable,
				isDismissed: getIsDismissed(isAnonymous, settings, hashIdLastDimissed),
			};
		},
		maxAge: 15 * 60 * 1000,
		isPageShellCritical: true,
	});

export const useAnnouncementBannerSettingsResource =
	(): UseResourceHookResponse<SettingsGetResource> =>
		useResource<SettingsGetResource>(announcementBannerSettingsResource);

export const useAnnouncementBannerSettingsV4Resource =
	(): UseResourceHookResponse<SettingsGetV4Resource> =>
		useResource<SettingsGetV4Resource>(announcementBannerSettingsV4Resource);
